import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Search, SlidersHorizontal, Play, Lock, FileText, Download, Loader2, GraduationCap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import api, { BACKEND } from "@/lib/api";
import { ytId } from "@/lib/format";
import ProductCard from "./ProductCard";

const CHIPS = [
  { label: "All Prices", range: [0, 2000] },
  { label: "Free", range: [0, 0] },
  { label: "Under ₹99", range: [0, 99] },
  { label: "Under ₹299", range: [0, 299] },
  { label: "Premium", range: [300, 2000] },
];

export function CourseView({ product, onBuy }) {
  const videoLinks = (product.video_links || []).map((u) => ({ url: u, vid: ytId(u) })).filter((v) => v.vid);
  const materials = product.materials || [];
  const [email, setEmail] = useState(() => { try { return localStorage.getItem("voogly_email") || ""; } catch { return ""; } });
  const [access, setAccess] = useState(null); // null = checking
  const [lesson, setLesson] = useState(0);
  const [emailErr, setEmailErr] = useState("");

  const check = async (em) => {
    try {
      const { data } = await api.get(`/course-access?product_id=${product.id}&email=${encodeURIComponent(em || "")}`);
      setAccess(data);
      if (data.has_access && em) { try { localStorage.setItem("voogly_email", em); } catch {} }
      setEmailErr(data.has_access ? "" : "is email par paid order nahi mila — khareedein ya doosra email try karein");
    } catch { setAccess({ has_access: false, free: false, need_email: true }); }
  };
  useEffect(() => { setAccess(null); setLesson(0); check(email); /* eslint-disable-next-line */ }, [product.id]);

  const unlocked = !!access?.has_access;
  const grouped = materials.reduce((acc, m) => {
    const s = m.subject || "General";
    (acc[s] = acc[s] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="rounded-3xl bg-ink pb-6 pt-2" data-testid="course-modal">
      <DialogHeader className="px-6 pt-5">
        <DialogTitle className="inline-flex items-center gap-2 font-display text-lg font-bold text-white text-left">
          <GraduationCap size={18} className="text-amber-400" /> {product.title}
          <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-400">Course · {videoLinks.length} lessons</span>
        </DialogTitle>
        <DialogDescription className="text-left text-sm text-slate-400">{product.description}</DialogDescription>
      </DialogHeader>

      {access === null ? (
        <div className="flex items-center justify-center py-16" data-testid="course-access-checking"><Loader2 size={26} className="animate-spin text-amber-500" /></div>
      ) : !unlocked ? (
        <div className="mx-6 mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 text-center" data-testid="course-locked">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/15 text-amber-400"><Lock size={24} /></span>
          <p className="mt-4 font-display text-lg font-extrabold text-white">Ye course unlock karke dekhein</p>
          {access?.free ? (
            <button onClick={() => check(email)} data-testid="course-open-free-btn" className="mt-4 rounded-full bg-amber-500 px-8 py-2.5 text-sm font-bold text-ink hover:bg-amber-400">Free — Abhi Dekhein</button>
          ) : (
            <>
              <p className="mt-2 text-sm text-slate-400">Jis email se khareeda tha, wo yahan likhein — ya abhi khareidhein ({product.price === 0 ? "Free" : `₹${Number(product.price).toLocaleString("en-IN")}`})</p>
              <div className="mx-auto mt-4 flex max-w-md flex-col gap-2 sm:flex-row">
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="apna@email.com" type="email" data-testid="course-email-input"
                  className="flex-1 rounded-full border border-white/10 bg-ink px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-500" />
                <button onClick={() => check(email)} data-testid="course-unlock-btn"
                  className="rounded-full bg-amber-500 px-6 py-2.5 text-sm font-bold text-ink hover:bg-amber-400">Unlock</button>
              </div>
              {emailErr && <p className="mt-2 text-xs font-bold text-red-400" data-testid="course-access-error">{emailErr}</p>}
              <button onClick={onBuy} data-testid="course-buy-btn"
                className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-ink border border-amber-500/50 px-8 py-3 font-display font-bold text-amber-400 hover:bg-amber-500 hover:text-ink">
                {product.price === 0 ? "Free me enroll karein" : `Buy Now — ₹${Number(product.price).toLocaleString("en-IN")}`}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="mx-6 mb-4 mt-2">
          {videoLinks.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-ink2" data-testid="course-player">
              <div className="aspect-video bg-black">
                <iframe
                  key={lesson}
                  src={`https://www.youtube.com/embed/${videoLinks[lesson]?.vid}?autoplay=0&rel=0`}
                  title={`Lesson ${lesson + 1}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
              <div className="max-h-64 space-y-1 overflow-y-auto p-3" data-lenis-prevent data-testid="course-lesson-list">
                {videoLinks.map((v, i) => (
                  <button key={i} onClick={() => setLesson(i)} data-testid={`course-lesson-${i}`}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm ${i === lesson ? "bg-amber-500/15 text-amber-300" : "text-slate-300 hover:bg-white/5"}`}>
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg font-mono text-[11px] font-bold ${i === lesson ? "bg-amber-500 text-ink" : "bg-white/10 text-slate-400"}`}>{i + 1}</span>
                    <span className="min-w-0 flex-1 truncate">Lesson {i + 1}</span>
                    {i === lesson && <Play size={13} fill="currentColor" />}
                  </button>
                ))}
              </div>
            </div>
          )}
          {materials.length > 0 && (
            <div className="mt-4 rounded-2xl border border-white/10 bg-ink2 p-4" data-testid="course-materials">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-amber-400">Study Material (PDF)</p>
              <div className="mt-3 space-y-4">
                {Object.entries(grouped).map(([subject, items]) => (
                  <div key={subject} data-testid={`course-subject-${subject.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="font-display text-sm font-extrabold text-white">{subject}</p>
                    <div className="mt-2 space-y-1.5">
                      {items.map((m, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 rounded-xl bg-white/5 px-3.5 py-2.5">
                          <div className="min-w-0">
                            <p className="truncate text-xs font-bold text-slate-200">{m.chapter || m.file_name}</p>
                            {m.chapter && <p className="truncate font-mono text-[10px] text-slate-500">{m.file_name}</p>}
                          </div>
                          <a href={`${m.file_path?.startsWith("/api") ? BACKEND : ""}${m.file_path}`} target="_blank" rel="noopener noreferrer" download
                            data-testid={`course-material-dl-${subject.toLowerCase().replace(/\s+/g, "-")}-${i}`}
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-500 px-3.5 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-400">
                            <Download size={12} /> Download
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PreviewGallery({ preview, onBuy }) {
  const imgs = [preview.cover_image_url, ...(preview.preview_images || [])].filter(Boolean);
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [preview?.id]);
  const src = `${imgs[idx]?.startsWith("/api") ? BACKEND : ""}${imgs[idx] || ""}`;
  return (
    <div>
      <div className="relative aspect-[4/3] bg-slate-100">
        {imgs.length > 0 ? (
          <img src={src} alt={`${preview.title} — preview ${idx + 1}`} loading="lazy" className="h-full w-full object-cover" data-testid="preview-gallery-main" />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-300"><Search size={40} /></div>
        )}
        {imgs.length > 1 && (
          <>
            <button onClick={() => setIdx((idx - 1 + imgs.length) % imgs.length)} data-testid="preview-gallery-prev" aria-label="Pichli image"
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow hover:bg-white"><ChevronLeft size={18} /></button>
            <button onClick={() => setIdx((idx + 1) % imgs.length)} data-testid="preview-gallery-next" aria-label="Agli image"
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow hover:bg-white"><ChevronRight size={18} /></button>
            <span className="absolute bottom-3 right-3 rounded-full bg-ink/80 px-3 py-1 font-mono text-[11px] font-bold text-amber-400" data-testid="preview-gallery-count">{idx + 1} / {imgs.length}</span>
          </>
        )}
      </div>
      {imgs.length > 1 && (
        <div className="flex gap-2 px-6 pt-4">
          {imgs.map((u, i) => (
            <button key={i} onClick={() => setIdx(i)} aria-label={`Preview ${i + 1}`} data-testid={`preview-gallery-thumb-${i}`}
              className={`h-14 w-12 overflow-hidden rounded-lg border-2 ${i === idx ? "border-amber-500" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={`${u.startsWith("/api") ? BACKEND : ""}${u}`} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <div className="p-6">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold text-left">{preview.title}</DialogTitle>
          <DialogDescription className="text-left text-sm leading-relaxed mt-2">{preview.description}</DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex items-center justify-between">
          <span className="font-display text-2xl font-extrabold">₹{preview.price === 0 ? "Free" : preview.price.toLocaleString("en-IN")}</span>
          <button onClick={onBuy} data-testid="preview-buy-btn"
            className="rounded-full bg-amber-500 px-6 py-2.5 text-sm font-bold text-ink hover:bg-amber-400">
            {preview.price === 0 ? "Get Free" : "Buy Now"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Catalog({ products, onBuy }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [range, setRange] = useState([0, 2000]);
  const [preview, setPreview] = useState(null);

  const categories = ["All", ...Array.from(new Set(products.map((p) => p.category).filter(Boolean)))];

  const filtered = products.filter((p) => {
    const inCat = category === "All" || p.category === category;
    const inPrice = p.price >= range[0] && p.price <= range[1];
    const q = search.trim().toLowerCase();
    const inSearch = !q || `${p.title} ${p.description} ${p.tags}`.toLowerCase().includes(q);
    return inCat && inPrice && inSearch;
  });

  const handleBuy = (p, mode) => {
    if (mode === "preview") { setPreview(p); return; }
    onBuy(p, "buy");
  };

  return (
    <section id="catalog" className="relative py-24 bg-cream">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search notes, subjects, tags…"
                data-testid="catalog-search-input"
                className="w-full rounded-full border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition"
              />
            </div>
            <div className="flex items-center gap-3 lg:w-80" data-testid="price-filter-slider">
              <SlidersHorizontal size={16} className="text-amber-600 shrink-0" />
              <Slider min={0} max={2000} step={1} value={range} onValueChange={(v) => setRange(v)} className="flex-1" />
              <span className="font-mono text-xs font-semibold text-slate-600 whitespace-nowrap">₹{range[0]}–₹{range[1]}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`category-tab-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${category === c ? "bg-ink text-amber-400" : "border border-slate-200 text-slate-600 hover:border-amber-500 hover:text-amber-600"}`}
              >
                {c}
              </button>
            ))}
            <span className="mx-2 h-4 w-px bg-slate-200 hidden sm:block" />
            {CHIPS.map((chip) => {
              const active = range[0] === chip.range[0] && range[1] === chip.range[1];
              return (
                <button
                  key={chip.label}
                  onClick={() => setRange(chip.range)}
                  data-testid={`price-chip-${chip.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${active ? "bg-amber-500 text-ink" : "border border-slate-200 text-slate-600 hover:border-amber-500 hover:text-amber-600"}`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>

        <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-slate-400" data-testid="catalog-count">
          {filtered.length} item{filtered.length === 1 ? "" : "s"} found
        </p>

        {filtered.length === 0 ? (
          <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-white/60 p-16 text-center" data-testid="catalog-empty-state">
            <p className="font-display text-xl font-bold text-slate-700">Kuch nahi mila</p>
            <p className="mt-2 text-sm text-slate-500">Filters reset karke dobara try karein.</p>
            <button
              onClick={() => { setSearch(""); setCategory("All"); setRange([0, 2000]); }}
              className="mt-6 rounded-full bg-ink px-6 py-2.5 text-sm font-bold text-amber-400"
              data-testid="catalog-reset-filters-btn"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} onBuy={handleBuy} index={i} />
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl rounded-3xl p-0 overflow-hidden" data-testid="preview-modal">
          {preview && (preview.ptype === "course" && ((preview.video_links || []).length || (preview.materials || []).length) ? (
            <CourseView product={preview} onBuy={() => { const pv = preview; setPreview(null); onBuy(pv, "buy"); }} />
          ) : (
            <PreviewGallery preview={preview} onBuy={() => { const pv = preview; setPreview(null); onBuy(pv, "buy"); }} />
          ))}
        </DialogContent>
      </Dialog>
    </section>
  );
}
